import { useState, type FormEvent } from 'react';

import type { RecordBody } from '@/shared/api/generated/model';
import { recordsCreate } from '@/shared/api/generated/records/records';
import { ApiError } from '@/shared/api/http';
import { Button } from '@/shared/ui/Button';
import { Dialog, DialogContent } from '@/shared/ui/Dialog';
import { Field } from '@/shared/ui/Field';
import { Input } from '@/shared/ui/Input';

export interface NewRecordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  typeID: string;
  onDone: (record: RecordBody) => void;
}

/**
 * Заведение записи.
 *
 * Спрашивается только название — остальное заполняется в редакторе. Форма из
 * двадцати полей в модальном окне не помещается, а главное, заполнять её
 * целиком перед первым сохранением никто не обязан: обязательные поля
 * проверяются при публикации.
 */
export function NewRecordDialog({ open, onOpenChange, typeID, onDone }: NewRecordDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && <Body typeID={typeID} onOpenChange={onOpenChange} onDone={onDone} />}
    </Dialog>
  );
}

function Body({ typeID, onOpenChange, onDone }: Omit<NewRecordDialogProps, 'open'>) {
  const [title, setTitle] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setError(undefined);
    setBusy(true);

    // Название кладётся в поле, которое тип считает заголовком; какое это
    // поле, решает сервер. Здесь оно уходит под ключом «title» только если
    // такое поле есть, — иначе запись заведётся без названия и получит его
    // при первом сохранении в редакторе.
    recordsCreate(typeID, {
      data: title.trim() === '' ? {} : { title: title.trim() },
    })
      .then((response) => {
        onDone(response.data as RecordBody);
        onOpenChange(false);
      })
      .catch((cause: unknown) => {
        setError(
          cause instanceof ApiError ? cause.detail : 'Не удалось создать. Попробуйте ещё раз.',
        );
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <DialogContent
      title="Новая запись"
      description="Остальные поля заполняются в редакторе. Обязательные проверяются при публикации."
      size="sm"
      dismissible={false}
      footer={
        <>
          <Button
            variant="outline"
            tone="neutral"
            type="button"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Отмена
          </Button>
          <Button type="submit" form="record-form" loading={busy}>
            Создать
          </Button>
        </>
      }
    >
      <form id="record-form" onSubmit={handleSubmit} noValidate>
        <Field label="Название" error={error} hint="Можно оставить пустым и заполнить потом.">
          <Input
            value={title}
            autoFocus
            placeholder="Как назвать запись"
            onChange={(event) => {
              setTitle(event.target.value);
            }}
          />
        </Field>
      </form>
    </DialogContent>
  );
}
